/** The co-marketing summary form: venue values to form values, and back to a patch. */
import type { VenueMarketingPatch } from '../../../../api/venue-marketing';
import type { VenueDetail } from '../../../../api/venues';

export type FollowingChoice = 'unknown' | 'yes' | 'no';

export interface SummaryDraft {
  rating: number | null;
  following: FollowingChoice;
  hubspotUrl: string;
}

export type SummaryValues = Pick<
  VenueDetail,
  'comarketingRating' | 'followingOnInstagram' | 'hubspotUrl'
>;

export const FOLLOWING_CHOICES: ReadonlyArray<{ value: FollowingChoice; label: string }> = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'yes', label: 'Yes, we follow them' },
  { value: 'no', label: 'No, we do not follow them' },
];

const CHOICE_FOR: Record<'true' | 'false' | 'null', FollowingChoice> = {
  true: 'yes',
  false: 'no',
  null: 'unknown',
};

export function followingFromChoice(choice: FollowingChoice): boolean | null {
  if (choice === 'unknown') return null;
  return choice === 'yes';
}

export function seedSummaryDraft(venue: SummaryValues): SummaryDraft {
  return {
    rating: venue.comarketingRating,
    following: CHOICE_FOR[String(venue.followingOnInstagram) as 'true' | 'false' | 'null'],
    hubspotUrl: venue.hubspotUrl ?? '',
  };
}

/**
 * Only the fields the person changed. `baseline` is the draft as it stood when
 * editing began, not the live venue: someone else may have changed another
 * field since, and an untouched field must not be sent back over their change.
 */
export function buildSummaryPatch(
  draft: SummaryDraft,
  baseline: SummaryDraft,
): VenueMarketingPatch {
  const patch: VenueMarketingPatch = {};
  if (draft.rating !== baseline.rating) patch.comarketingRating = draft.rating;
  if (draft.following !== baseline.following) {
    patch.followingOnInstagram = followingFromChoice(draft.following);
  }
  const link = draft.hubspotUrl.trim();
  if (link !== baseline.hubspotUrl.trim()) patch.hubspotUrl = link === '' ? null : link;
  return patch;
}
